import React, { useContext, useEffect, useMemo } from 'react';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import AssessmentPeriodListWithStatus from '@/areas/culture-hub/components/shared/period-selector/AssessmentPeriodListWithStatus';
import { AssessmentPeriodRead } from '@/store/generated/portal/assessments';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/common/ui/select';

export type LeadershipTeam = {
  id: number;
  name: string;
  assessments?: number[];
};

export type UseCultureHubTopBarControlsProps = {
  showSelector: boolean;
  selectedTeamIdGlobal?: number;
  leadershipTeams: LeadershipTeam[];
  selectedAssessmentPeriodId: number | null;
  filteredAssessmentPeriods?: AssessmentPeriodRead[];
  isLoadingPeriods: boolean;
  onTeamChange: (id: number | null) => void;
  onAssessmentPeriodChange: (value: string | number | undefined, period?: AssessmentPeriodRead) => void;
  extraControls?: React.ReactNode;
};

export function useCultureHubTopBarControls({
  showSelector,
  selectedTeamIdGlobal,
  leadershipTeams,
  selectedAssessmentPeriodId,
  filteredAssessmentPeriods,
  isLoadingPeriods,
  onTeamChange,
  onAssessmentPeriodChange,
  extraControls,
}: UseCultureHubTopBarControlsProps) {
  const { setTopBarContent } = useContext(TopBarContext);

  const topBarControls = useMemo(
    () => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {showSelector && (
          <div style={{ minWidth: 220 }}>
            <Select
              value={selectedTeamIdGlobal != null ? String(selectedTeamIdGlobal) : undefined}
              onValueChange={(value) => {
                const id = value ? Number(value) : null;
                onTeamChange(id);
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue>
                  <span>{leadershipTeams?.find(t => String(t.id) === String(selectedTeamIdGlobal))?.name ?? '-'}</span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {leadershipTeams?.map(team => (
                  <SelectItem key={String(team.id)} value={String(team.id)}>
                    <span>{team.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <AssessmentPeriodListWithStatus
          selectedAssessmentPeriodId={selectedAssessmentPeriodId}
          assessmentPeriods={filteredAssessmentPeriods}
          isLoading={isLoadingPeriods}
          onChange={onAssessmentPeriodChange}
        />

        {extraControls}
      </div>
    ),
    [
      showSelector,
      selectedTeamIdGlobal,
      leadershipTeams,
      selectedAssessmentPeriodId,
      filteredAssessmentPeriods,
      isLoadingPeriods,
      onTeamChange,
      onAssessmentPeriodChange,
      extraControls,
    ]
  );

  useEffect(() => {
    setTopBarContent(topBarControls);
    const raf = requestAnimationFrame(() => setTopBarContent(topBarControls));
    return () => {
      if (raf) cancelAnimationFrame(raf);
      setTopBarContent(null);
    };
  }, [topBarControls, setTopBarContent]);
}
